import React, { useState, useEffect } from "react";
import { BiInfoCircle } from "react-icons/bi";
import { ExternalLink, Loader2, X, Info } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import { useToast } from "../../../hooks/use-toast";

import { ConnectStore, DisconnectStore, StoreSync } from "../../../slices/store";
import { setField } from "../../../slices/auth";

// const REDIRECT_URI = `${window.location.origin}/auth/sign-up/step/4`;

const AccountConnectionStep = ({ onNext }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, currentUser } = useSelector(state => state?.auth || {});
  const { storeSyncLoading } = useSelector(state => state?.store || {});
  const { toast } = useToast();

  const queryParams = new URLSearchParams(location.search);
  const authCode = queryParams.get('spapi_oauth_code') || '';
  const sellerId = queryParams.get('selling_partner_id') || '';
  const stateParam = queryParams.get('state') || '';
  let parsedState = {};
  try {
    if (stateParam) {
      parsedState = JSON.parse(decodeURIComponent(stateParam));
    }
  } catch (err) {
    console.error("Failed to parse stateParam:", err, stateParam);
    parsedState = {};
  }

  const email = currentUser?.email || user?.email || ''
  const STATE_VALUE = {
    email,
    key: 'spAPIStoreConnect'
  }

  const stringifiedStateValue = JSON.stringify(STATE_VALUE)
  const [connections, setConnections] = useState({
    amazon: false,
    walmart: false,
  });
  const [isConnecting, setIsConnecting] = useState({ amazon: false, walmart: false });

  const handleConnect = async (platform) => {
    setIsConnecting(prev => ({ ...prev, [platform]: true }));
    const url = `https://sellercentral.amazon.com/apps/authorize/consent?application_id=${process.env.SELLING_PARTNER_APP_APPLICATION_ID}&state=${stringifiedStateValue}&version=beta&redirect_uri=${process.env.REDIRECT_URI}`;
    window.open(url, '_self');
  };

  const handleDisconnect = (platform) => {
    dispatch(DisconnectStore({}))
      .unwrap()
      .then(() => {
        setConnections(prev => ({ ...prev, [platform]: false }));
        // Notify other tabs about the disconnection
        localStorage.setItem('storeConnectionStatus', JSON.stringify({
          isConnected: false,
          platform: 'amazon',
          timestamp: Date.now()
        }));

        toast({
          title: "Account Disconnected",
          description: `${platform.charAt(0).toUpperCase() + platform.slice(1)} account has been disconnected.`,
          variant: "destructive",
        });

        dispatch(setField({
          module: 'user',
          field: 'sw_store_flag',
          value: false
        }));
        dispatch(setField({
          module: 'currentUser',
          field: 'sw_store_flag',
          value: false
        }));
      })
      .catch((error) => {
        console.log('Error while disconnecting store: ', error)
      });
  };

  const handleContinue = () => {
    // Store completion data for other tabs
    dispatch(StoreSync({}))
      .unwrap()
      .then(() => {
        dispatch(setField({
          module: 'currentUser',
          field: 'store_sync_started_at',
          value: Date.now()
        }));
        const completionData = {
          timestamp: Date.now().toString(),
          connectedAccounts: connections,
          accountsConnected: true
        };
        localStorage.setItem("accountConnectionCompleted", JSON.stringify(completionData));
        onNext({ connectedAccounts: connections });
      })
      .catch((error) => {
        console.log('Error while syncing store: ', error)
      });
  };

  useEffect(() => {

    if (authCode && sellerId && parsedState?.key === STATE_VALUE?.key && !currentUser.sw_store_flag) {
      setIsConnecting(prev => ({ ...prev, amazon: true }));
      dispatch(ConnectStore({ authCode, sellerId }))
        .unwrap()
        .then(() => {
          toast({
            title: "Connection Successful",
            description: `Your Amazon seller account has been connected.`,
          });
          dispatch(setField({
            module: 'user',
            field: 'sw_store_flag',
            value: true
          }));
          dispatch(setField({
            module: 'currentUser',
            field: 'sw_store_flag',
            value: true
          }));
          setConnections(prev => ({ ...prev, amazon: true }));
          setIsConnecting(prev => ({ ...prev, amazon: false }));

          // Notify other tabs about the successful connection
          localStorage.setItem('storeConnectionStatus', JSON.stringify({
            isConnected: true,
            platform: 'amazon',
            timestamp: Date.now()
          }));

          navigate(location.pathname, { replace: true })
        })
        .catch((error) => {
          console.log('Error while connecting store: ', error)

          setConnections(prev => ({ ...prev, amazon: false }));
          setIsConnecting(prev => ({ ...prev, amazon: false }));
        });
    }

  }, [authCode, sellerId, dispatch])

  // Listen for store connection status changes from other tabs
  useEffect(() => {
    const handleStoreConnectionChange = (e) => {
      if (e.key === 'storeConnectionStatus' && e.newValue) {
        try {
          const { isConnected, platform } = JSON.parse(e.newValue);
          if (platform === 'amazon') {
            setConnections(prev => ({ ...prev, amazon: isConnected }));
            // Update the Redux store to reflect the connection status
            dispatch(setField({
              module: 'user',
              field: 'sw_store_flag',
              value: isConnected
            }));
            dispatch(setField({
              module: 'currentUser',
              field: 'sw_store_flag',
              value: isConnected
            }));
          }
        } catch (error) {
          console.error('Error parsing store connection status:', error);
        }
      }
    };

    // Listen for completion from other tabs
    const handleCompletion = (e) => {
      if (e.key === "accountConnectionCompleted" && e.newValue) {
        try {
          const completionData = JSON.parse(e.newValue);
          if (completionData.accountsConnected) {
            localStorage.removeItem("accountConnectionCompleted");
            onNext({ connectedAccounts: completionData.connectedAccounts });
          }
        } catch (error) {
          console.error("Error parsing completion data:", error);
        }
      }
    };

    window.addEventListener('storage', handleStoreConnectionChange);
    window.addEventListener('storage', handleCompletion);

    return () => {
      window.removeEventListener('storage', handleStoreConnectionChange);
      window.removeEventListener('storage', handleCompletion);
    };
  }, [onNext, dispatch]);

  return (
    <div className="account-connection-container">
      <div className="header animate-fade-in">
        <h2 className="inner-title">
          Connect Your Seller Accounts
        </h2>
        <p className="subtitle">
          Connect your marketplace accounts to sync products, orders, and inventory automatically.
        </p>
      </div>

      <div className="account-grid">
        {/* Amazon Connection */}
        <div className="account-card">
          <div className="account-header">
            <div className="account-info">
              <div className="account-icon amazon">
                <span className="account-icon-text">A</span>
              </div>
              <div className="account-details">
                <h3 className="mb-1">Amazon Seller Central</h3>
                <p className="font-medium lh-1.5">Connect your Amazon seller account</p>
              </div>
            </div>
            <div className={`status ${(currentUser?.sw_store_flag || user?.sw_store_flag) || connections.amazon ? 'connected' : 'not-connected'}`}>
              {(currentUser?.sw_store_flag || user?.sw_store_flag) || connections.amazon ? "Connected" : "Not Connected"}
            </div>
          </div>

          <div className="info-box">
            <div className="info-content">
              <Info className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <div className="info-text">
                <p>What we'll sync:</p>
                <ul>
                  <li>Product listings and inventory</li>
                  <li>Order information and status</li>
                  <li>Sales analytics and performance</li>
                </ul>
              </div>
            </div>
          </div>

          {(currentUser?.sw_store_flag || user?.sw_store_flag) || connections.amazon ? (
            <div className="actions-column">
              <div className="success">
                <span className="success-icon">✓</span>
                <span className="success-text">Successfully Connected</span>
              </div>
              <button
                onClick={() => handleDisconnect('amazon')}
                className="btn-outline bg-background"
              >
                <X className="w-4 h-4 mr-1" />
                Disconnect Account
              </button>
            </div>
          ) : (
            <div>
              <button
                onClick={() => handleConnect('amazon')}
                disabled={isConnecting.amazon}
                className="btn-primary w-100 font-semibold"
              >
                {isConnecting.amazon ? (<Loader2 className="w-5 h-5 mr-1 animate-spin" />) : (<ExternalLink className="w-4 h-4 mr-2" />)}
                {isConnecting.amazon ? "Connecting..." : "Connect Amazon Account"}
              </button>
            </div>
          )}
        </div>

        {/* Walmart Connection */}
        <div className="account-card coming-soon">
          <div className="badge coming-soon">
            Coming Soon
          </div>
          <div className="account-header">
            <div className="account-info">
              <div className="account-icon walmart">
                <span className="account-icon-text">W</span>
              </div>
              <div className="account-details">
                <h3>Walmart Marketplace</h3>
                <p>Connect your Walmart seller account</p>
              </div>
            </div>
            <div className="status coming-soon">
              Coming Soon
            </div>
          </div>

          <div className="info-box">
            <div className="info-content">
              <Info className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <div className="info-text">
                <p>What we'll sync:</p>
                <ul>
                  <li>Product catalog and pricing</li>
                  <li>Inventory levels and updates</li>
                  <li>Order management and fulfillment</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            disabled={true}
            className="btn-primary w-100"
          >
            Coming Soon
          </button>
        </div>
      </div>

      <div className="actions-center">
        <button
          disabled={!currentUser.sw_store_flag || storeSyncLoading}
          onClick={handleContinue}
          className="btn-primary"
        >
          {storeSyncLoading ? (
            <>
              <Spin /> Processing
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export { AccountConnectionStep };
